import React, { useEffect, useState } from 'react';
import { Storage } from 'aws-amplify';
import { useLocation } from '@reach/router';
import {
  fetchUserBySub,
  listEligibleRegistrations, listCourses,
} from '../../components/DataStore/Services';
import CourseService from
  '../../components/Learner/Services/CourseService';
import {
  Course, CourseRegistration, EnterpriseProfile, User,
} from '../../models';
import { getCurrentUser } from '../../components/Shared/Services/AuthService';
import { DatastoreUser } from '../../types/commons';
import { getEnterpriseProfile, listPrivateCoursesByEnterpriseId } from '../../components/GraphQL/Services';
import { CourseStatus } from '../../API';

const courseService = new CourseService();

export type CourseRegistrationsWithCourses = Array<CourseRegistration & {
  course: Course
}>

export enum ContextState {
  PRISTINE, // Context state isn't set yet
  READY, // Context state has been set
}

export type EnterpriseInfo = EnterpriseProfile & {
  logo: string
}

export type LearnerContextType = {
  loading: boolean,
  setLoading: (b: boolean) => void,
  showCourseSwitcher: boolean,
  setShowCourseSwitcher: (b: boolean) => void,
  profileInfo: DatastoreUser,
  setProfileInfo: (u: DatastoreUser) => void,
  courseRegistrations: CourseRegistrationsWithCourses,
  setCourseRegistrations: (c: CourseRegistrationsWithCourses) => void,
  setCourseRegistrationsWithStatus:(c: any) => void,
  courseRegistrationsWithStatus:any,
  contextState: ContextState,
  setContextState: (c: ContextState) => void,
  enterpriseInfo: EnterpriseInfo,
  setEnterpriseInfo: any,
}

const defaultState = {
  loading: true,
  setLoading: () => {},
  showCourseSwitcher: false,
  setShowCourseSwitcher: () => {},
  profileInfo: {} as DatastoreUser,
  setProfileInfo: () => {},
  courseRegistrations: [] as CourseRegistrationsWithCourses,
  setCourseRegistrations: () => {},
  setCourseRegistrationsWithStatus: () => {},
  courseRegistrationsWithStatus: [] as any,
  contextState: ContextState.PRISTINE,
  setContextState: () => {},
  enterpriseInfo: {} as EnterpriseInfo,
  setEnterpriseInfo: () => {},
};
const LearnerContext = React.createContext<LearnerContextType>(defaultState);

type Props = {
  children: React.ReactNode,
}

/**
 *
 *
 * @param {*} props
 * @return {*}
 */
const LearnerProvider: React.FC<Props> = (props) => {
  const { children } = props;
  const [loading, setLoading] = useState<boolean>(true);
  const [showCourseSwitcher, setShowCourseSwitcher] = useState<boolean>(false);
  const [profileInfo, setProfileInfo] = useState<DatastoreUser>({} as DatastoreUser);
  const [courseRegistrationsWithStatus,
    setCourseRegistrationsWithStatus] = useState<any[]>([]);
  const [courseRegistrations, setCourseRegistrations] = useState<
  CourseRegistrationsWithCourses>([] as CourseRegistrationsWithCourses);
  const [contextState, setContextState] = useState<ContextState>(ContextState.PRISTINE);
  const [enterpriseInfo, setEnterpriseInfo] = useState<EnterpriseInfo>({} as EnterpriseInfo);

  const location = useLocation();

  const fetchPrivateCourses = async (enterpriseId:string) => {
    try {
      const courses = await listPrivateCoursesByEnterpriseId(enterpriseId);
      const eligibleCourses = courses.filter((course) => course?.status === CourseStatus.AVAILABLE);
      return eligibleCourses;
    } catch (e) {
      return [];
    }
  };

  const fetchPublicCourses = async () : Promise<Course[]> => {
    const courses = await listCourses();
    const eligibleCourses = courses.filter((course: Course) => course.source !== 'shawacademy.com');
    return eligibleCourses;
  };

  const getCoursesWithRegistrationData = (courses:Course[], registrations:CourseRegistration[]) => {
    const registeredCourses = [] as CourseRegistrationsWithCourses;
    const registrationsMap = new Map();
    registrations.forEach((registration) => {
      registrationsMap.set(registration.courseID, registration);
    });
    courses.forEach((course) => {
      const courseId = course.id;
      const registration = registrationsMap.get(courseId);
      if (registration) {
        registeredCourses.push({
          ...registration,
          course,
        });
      }
    });
    return registeredCourses;
  };

  const fetchRegisteredCourses = async (user:User) => {
    const userId = user.id;
    const enterpriseId = user?.enterpriseID || '';
    const registrations = await listEligibleRegistrations(userId) as CourseRegistration[];
    const coursePromises = [
      fetchPublicCourses(),
      fetchPrivateCourses(enterpriseId),
    ];
    const [publicCourses, privateCourses] = await Promise.all(coursePromises);
    const allCourses = [...publicCourses, ...privateCourses];
    const coursesWithRegistrationData = getCoursesWithRegistrationData(allCourses, registrations);
    setLoading(false);
    courseService.setCourses(allCourses);
    setCourseRegistrations(coursesWithRegistrationData);
    const coursesWithRegistrationStatus = courseService
      .mergeCoursesWithRegistrations(registrations);
    courseService.setCourses(coursesWithRegistrationStatus);
    setCourseRegistrationsWithStatus(coursesWithRegistrationStatus);
    setContextState(ContextState.READY);
  };

  const fetchEnterpriseInfo = async (user:User) => {
    const enterpriseId = user?.enterpriseID;
    const companyProfileInfo = await getEnterpriseProfile(enterpriseId);
    if (companyProfileInfo?.id) {
      const { id } = companyProfileInfo;
      const fileKey = `enterprises/${id}/logo`;
      const list = await Storage.list(fileKey, { level: 'public' });
      const file = list?.length > 0 ? list[0] : null;
      const url = file?.key ? await Storage.get(file?.key, { level: 'public' }) as string : '';
      setEnterpriseInfo({
        ...companyProfileInfo,
        logo: url,
      });
    }
  };

  const loadDependencies = async () => {
    const user = await getCurrentUser();
    const subId = user.attributes.sub;
    const id = user?.attributes?.['custom:active_user'];
    const userRes = await fetchUserBySub(subId, id);
    fetchEnterpriseInfo(userRes);
    fetchRegisteredCourses(userRes);
    const list = await Storage.list(userRes.id, { level: 'protected' });
    const file = list?.length > 0 ? list[0] : null;
    const url = file?.key ? await Storage.get(file?.key, { level: 'protected' }) as string : '';
    setProfileInfo({
      ...userRes,
      profilePicture: url,
    });
  };

  useEffect(() => {
    loadDependencies();
  }, [location]);

  return (
    <LearnerContext.Provider
      value={{
        loading,
        setLoading,
        showCourseSwitcher,
        setShowCourseSwitcher,
        profileInfo,
        courseRegistrations,
        setProfileInfo,
        setCourseRegistrations,
        setCourseRegistrationsWithStatus,
        courseRegistrationsWithStatus,
        setContextState,
        contextState,
        setEnterpriseInfo,
        enterpriseInfo,
      }}
    >
      {children}
    </LearnerContext.Provider>
  );
};

export default LearnerContext;

export { LearnerProvider };
